/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */

import {moneyToApi} from './format';

// Logica pura dei moduli di configurazione: dalla stringa digitata al valore
// per l'API, e confronto con l'originale per inviare solo i campi cambiati.

export const VAT_RATES = [0, 4, 5, 10, 22];
export const FUEL_TYPES = [
  'BENZINA',
  'DIESEL',
  'GPL',
  'METANO',
  'IBRIDA',
  'ELETTRICA',
  'ALTRO',
];

// Come Formats::CODE, Formats::RATE e SettingsSchema::ACCOUNT del server.
const CODE = /^[A-Z][A-Z0-9_]{1,39}$/;
const ACCOUNT = /^[A-Za-z0-9.-]{1,20}$/;

export type Parsed<T> = {ok: true; value: T} | {ok: false};

const ok = <T>(value: T): Parsed<T> => ({ok: true, value});
const bad: Parsed<never> = {ok: false};

/** Tariffa al km digitata ("0,42") -> "0.4200", o null se non è nel formato. */
export const rateToApi = (text: string | null | undefined): string | null => {
  const match = /^(\d{1,2})(?:[.,](\d{1,4}))?$/.exec((text ?? '').trim());
  if (!match) return null;
  return `${Number(match[1])}.${(match[2] ?? '').padEnd(4, '0')}`;
};

/** Importo facoltativo: vuoto vale null, altrimenti "12.50" o non valido. */
const optionalMoney = (text: string): Parsed<string | null> => {
  if (text.trim() === '') return ok(null);
  const value = moneyToApi(text);
  return value === null ? bad : ok(value);
};

const intInRange = (text: string, min: number, max: number): Parsed<number> => {
  const trimmed = text.trim();
  if (!/^\d{1,6}$/.test(trimmed)) return bad;
  const value = Number(trimmed);
  return value >= min && value <= max ? ok(value) : bad;
};

/** Parole chiave separate da virgole: in minuscolo come fa il server, senza doppioni. */
export const parseKeywords = (text: string): Parsed<string[]> => {
  const words = text
    .split(',')
    .map((w) => w.trim().toLowerCase())
    .filter((w) => w !== '');
  const unique = words.filter((w, i) => words.indexOf(w) === i);
  if (unique.length > 50 || unique.some((w) => w.length > 40)) return bad;
  return ok(unique);
};

// ---------------------------------------------------------------- categorie

export interface CategoryRecord {
  code: string;
  name: string;
  glAccount: string | null;
  defaultVatRate: number | null;
  vatDeductiblePct: number;
  requiresReceiptAbove: string | null;
  maxPerItem: string | null;
  maxPerDay: string | null;
  keywords: string[];
  active: boolean;
  sortOrder: number;
}

export interface CategoryForm {
  code: string;
  name: string;
  glAccount: string;
  defaultVatRate: number | null;
  vatDeductiblePct: string;
  requiresReceiptAbove: string;
  maxPerItem: string;
  maxPerDay: string;
  keywords: string;
  active: boolean;
  sortOrder: string;
}

export const emptyCategoryForm = (): CategoryForm => ({
  code: '',
  name: '',
  glAccount: '',
  defaultVatRate: null,
  vatDeductiblePct: '100',
  requiresReceiptAbove: '',
  maxPerItem: '',
  maxPerDay: '',
  keywords: '',
  active: true,
  sortOrder: '0',
});

export const categoryToForm = (c: CategoryRecord): CategoryForm => ({
  code: c.code,
  name: c.name,
  glAccount: c.glAccount ?? '',
  defaultVatRate: c.defaultVatRate,
  vatDeductiblePct: String(c.vatDeductiblePct),
  requiresReceiptAbove: c.requiresReceiptAbove ?? '',
  maxPerItem: c.maxPerItem ?? '',
  maxPerDay: c.maxPerDay ?? '',
  keywords: (c.keywords ?? []).join(', '),
  active: c.active,
  sortOrder: String(c.sortOrder),
});

export type Values = Record<string, unknown>;

/**
 * Valori da inviare per un form categoria e nomi dei campi non validi.
 * Il codice è incluso: chi aggiorna lo toglie (non cambia dopo la creazione).
 */
export const categoryValues = (
  f: CategoryForm,
): {values: Values; invalid: string[]} => {
  const values: Values = {};
  const invalid: string[] = [];
  const take = <T>(key: string, parsed: Parsed<T>) => {
    if (parsed.ok) values[key] = parsed.value;
    else invalid.push(key);
  };
  take('code', CODE.test(f.code.trim()) ? ok(f.code.trim()) : bad);
  const name = f.name.trim();
  take('name', name.length >= 1 && name.length <= 100 ? ok(name) : bad);
  const gl = f.glAccount.trim();
  take('glAccount', gl.length <= 20 ? ok(gl === '' ? null : gl) : bad);
  take(
    'defaultVatRate',
    f.defaultVatRate === null || VAT_RATES.includes(f.defaultVatRate)
      ? ok(f.defaultVatRate)
      : bad,
  );
  take('vatDeductiblePct', intInRange(f.vatDeductiblePct, 0, 100));
  take('requiresReceiptAbove', optionalMoney(f.requiresReceiptAbove));
  take('maxPerItem', optionalMoney(f.maxPerItem));
  take('maxPerDay', optionalMoney(f.maxPerDay));
  take('keywords', parseKeywords(f.keywords));
  values.active = f.active;
  take('sortOrder', intInRange(f.sortOrder, 0, 9999));
  return {values, invalid};
};

const sameValue = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/** Solo i campi della categoria cambiati rispetto all'originale (mai il codice). */
export const categoryPatch = (
  original: CategoryRecord,
  form: CategoryForm,
): {patch: Values; invalid: string[]} => {
  const current = categoryValues(form);
  const before = categoryValues(categoryToForm(original)).values;
  const patch: Values = {};
  Object.keys(current.values).forEach((key) => {
    if (key !== 'code' && !sameValue(current.values[key], before[key])) {
      patch[key] = current.values[key];
    }
  });
  return {patch, invalid: current.invalid.filter((k) => k !== 'code')};
};

// ------------------------------------------------------------------ veicoli

export interface VehicleRecord {
  name: string;
  plate: string | null;
  fuelType: string | null;
  active: boolean;
}

export interface VehicleForm {
  name: string;
  plate: string;
  fuelType: string | null;
  active: boolean;
}

export const vehicleToForm = (v: VehicleRecord): VehicleForm => ({
  name: v.name,
  plate: v.plate ?? '',
  fuelType: v.fuelType,
  active: v.active,
});

export const vehicleValues = (
  f: VehicleForm,
): {values: Values; invalid: string[]} => {
  const values: Values = {};
  const invalid: string[] = [];
  const name = f.name.trim();
  if (name.length >= 1 && name.length <= 100) values.name = name;
  else invalid.push('name');
  const plate = f.plate.trim();
  if (plate.length <= 20) values.plate = plate === '' ? null : plate;
  else invalid.push('plate');
  if (f.fuelType === null || FUEL_TYPES.includes(f.fuelType)) {
    values.fuelType = f.fuelType;
  } else {
    invalid.push('fuelType');
  }
  values.active = f.active;
  return {values, invalid};
};

export const vehiclePatch = (
  original: VehicleRecord,
  form: VehicleForm,
): {patch: Values; invalid: string[]} => {
  const current = vehicleValues(form);
  const before = vehicleValues(vehicleToForm(original)).values;
  const patch: Values = {};
  Object.keys(current.values).forEach((key) => {
    if (!sameValue(current.values[key], before[key])) {
      patch[key] = current.values[key];
    }
  });
  return {patch, invalid: current.invalid};
};

// -------------------------------------------------------------- impostazioni

export type FieldType =
  | 'int'
  | 'bool'
  | 'money'
  | 'rate'
  | 'account'
  | 'url'
  | 'baseUrl';

export interface SettingField {
  key: string;
  type: FieldType;
  label: string; // chiave di lang string
  min?: number;
  max?: number;
  nullable?: boolean;
}

export interface SettingGroup {
  key: string;
  title: string; // chiave di lang string
  fields: SettingField[];
}

// Specchio di SettingsSchema::DEFAULTS/RULES del server (che resta l'autorità:
// rilegge e valida tutto). Le chiavi sono quelle dell'API.
export const SETTING_GROUPS: SettingGroup[] = [
  {
    key: 'policy',
    title: 'expensa.set_group_policy',
    fields: [
      {
        key: 'maxAgeDays',
        type: 'int',
        min: 1,
        max: 3650,
        label: 'expensa.set_policy_max_age',
      },
      {
        key: 'duplicateWindowDays',
        type: 'int',
        min: 0,
        max: 30,
        label: 'expensa.set_policy_duplicate_window',
      },
      {
        key: 'weekendWarning',
        type: 'bool',
        label: 'expensa.set_policy_weekend',
      },
      {
        key: 'descriptionRequiredAbove',
        type: 'money',
        nullable: true,
        label: 'expensa.set_policy_description_above',
      },
      {
        key: 'financeApprovalAbove',
        type: 'money',
        nullable: true,
        label: 'expensa.set_policy_finance_above',
      },
    ],
  },
  {
    key: 'perDiem',
    title: 'expensa.set_group_per_diem',
    fields: [
      {key: 'italyDaily', type: 'money', label: 'expensa.set_per_diem_italy'},
      {key: 'abroadDaily', type: 'money', label: 'expensa.set_per_diem_abroad'},
      {
        key: 'minHours',
        type: 'int',
        min: 0,
        max: 24,
        label: 'expensa.set_per_diem_min_hours',
      },
    ],
  },
  {
    key: 'mileage',
    title: 'expensa.set_group_mileage',
    fields: [
      {
        key: 'defaultRatePerKm',
        type: 'rate',
        label: 'expensa.set_mileage_default_rate',
      },
      {
        key: 'maxRatePerKm',
        type: 'rate',
        label: 'expensa.set_mileage_max_rate',
      },
    ],
  },
  {
    key: 'accounting',
    title: 'expensa.set_group_accounting',
    fields: [
      {
        key: 'vatCreditAccount',
        type: 'account',
        label: 'expensa.set_accounting_vat',
      },
      {
        key: 'employeePayableAccount',
        type: 'account',
        label: 'expensa.set_accounting_employee',
      },
      {
        key: 'companyCardAccount',
        type: 'account',
        label: 'expensa.set_accounting_card',
      },
    ],
  },
  {
    key: 'notifications',
    title: 'expensa.set_group_notifications',
    fields: [
      {
        key: 'enabled',
        type: 'bool',
        label: 'expensa.set_notifications_enabled',
      },
      {
        key: 'baseUrl',
        type: 'baseUrl',
        label: 'expensa.set_notifications_base_url',
      },
    ],
  },
  {
    key: 'preservation',
    title: 'expensa.set_group_preservation',
    fields: [
      {key: 'enabled', type: 'bool', label: 'expensa.set_preservation_enabled'},
      {key: 'tsaUrl', type: 'url', label: 'expensa.set_preservation_tsa'},
      {
        key: 'retentionYears',
        type: 'int',
        min: 1,
        max: 50,
        label: 'expensa.set_preservation_retention',
      },
    ],
  },
];

/** Chiave del messaggio d'errore per un campo non valido. */
export const invalidKey = (field: SettingField): string => {
  switch (field.type) {
    case 'money':
      return 'expensa.invalid_amount';
    case 'rate':
      return 'expensa.set_invalid_rate';
    case 'account':
      return 'expensa.set_invalid_account';
    case 'url':
    case 'baseUrl':
      return 'expensa.set_invalid_url';
    default:
      return 'expensa.set_invalid_int';
  }
};

/**
 * Valore digitato (o booleano) di un campo -> valore per l'API.
 * Le stesse regole di SettingsSchema::violation().
 */
export const parseSetting = (
  field: SettingField,
  input: string | boolean | null | undefined,
): Parsed<string | number | boolean | null> => {
  if (field.type === 'bool')
    return typeof input === 'boolean' ? ok(input) : bad;
  const text = typeof input === 'string' ? input : '';
  switch (field.type) {
    case 'int':
      return intInRange(text, field.min ?? 0, field.max ?? 999999);
    case 'money': {
      if (text.trim() === '') return field.nullable ? ok(null) : bad;
      // Come MONEY delle impostazioni: al più 7 cifre intere.
      const value = /^\d{1,7}(?:[.,]\d{1,2})?$/.test(text.trim())
        ? moneyToApi(text)
        : null;
      return value === null ? bad : ok(value);
    }
    case 'rate': {
      const value = rateToApi(text);
      return value === null ? bad : ok(value);
    }
    case 'account':
      return ACCOUNT.test(text.trim()) ? ok(text.trim()) : bad;
    case 'url':
    case 'baseUrl': {
      const url = text.trim();
      if (url.length > 300) return bad;
      if (url === '') return ok('');
      if (!/^https?:\/\//.test(url)) return bad;
      return field.type === 'baseUrl' && /\s/.test(url) ? bad : ok(url);
    }
  }
  return bad;
};

export type SettingsRecord = Record<string, Record<string, unknown>>;
export type SettingsForm = Record<string, Record<string, string | boolean>>;

/** Impostazioni dell'API -> valori dei campi del form (stringhe e booleani). */
export const settingsToForm = (settings: SettingsRecord): SettingsForm => {
  const form: SettingsForm = {};
  SETTING_GROUPS.forEach((group) => {
    form[group.key] = {};
    group.fields.forEach((field) => {
      const value = settings[group.key]?.[field.key];
      form[group.key][field.key] =
        field.type === 'bool'
          ? value === true
          : value === null || value === undefined
          ? ''
          : String(value);
    });
  });
  return form;
};

/**
 * Patch con i soli campi cambiati rispetto alle impostazioni originali,
 * `{gruppo: {chiave: valore}}`, e l'elenco dei campi non validi
 * (`gruppo.chiave`). Un gruppo senza modifiche non compare.
 */
export const settingsPatch = (
  original: SettingsRecord,
  form: SettingsForm,
): {patch: Record<string, Record<string, unknown>>; invalid: string[]} => {
  const before = settingsToForm(original);
  const patch: Record<string, Record<string, unknown>> = {};
  const invalid: string[] = [];
  SETTING_GROUPS.forEach((group) => {
    group.fields.forEach((field) => {
      const now = parseSetting(field, form[group.key]?.[field.key]);
      if (!now.ok) {
        invalid.push(`${group.key}.${field.key}`);
        return;
      }
      const was = parseSetting(field, before[group.key][field.key]);
      if (was.ok && was.value === now.value) return;
      patch[group.key] = {...patch[group.key], [field.key]: now.value};
    });
  });
  return {patch, invalid};
};
